import type { QueryClient } from '@tanstack/react-query';

/**
 * Query keys grouped by the action that dirties them.
 *
 * WHY a module rather than inline `invalidateQueries` calls: an invalidation
 * naming a key that no query uses is silent. TanStack matches nothing,
 * resolves the promise, and the pre-mutation value keeps rendering; neither
 * tsc nor eslint can see the mismatch. Two invalidations in this repo named
 * `['me']` — a key no query has ever used — and stayed dead long enough for a
 * claimed invite to stop showing the new organization until a page reload.
 *
 * Collecting the sets here lets a test seed a QueryClient with the keys the
 * app really registers and assert each one actually got invalidated, which is
 * the only way this class of bug is detectable before a user reports it.
 */

export type QueryKey = readonly unknown[];

/** Identity, memberships, role, org switcher. Keyed `['profile']` — see useProfile. */
export const PROFILE_KEY: QueryKey = ['profile'];
export const ENROLLMENTS_KEY: QueryKey = ['enrollments'];
// Deliberately not exported as an invalidation target: no query is rooted at
// ['event_registrations']. A roster lives under ['events', id, 'registrations']
// and a learner's own registrations arrive inside the ['enrollments'] payload,
// so invalidating this root is the dead-key bug this module exists to prevent.
export const EVENT_ROSTER_KEY = (eventId: number): QueryKey => ['events', eventId, 'registrations'];
export const ORGANIZATIONS_KEY: QueryKey = ['organizations'];

export function invalidateAll(queryClient: QueryClient, keys: readonly QueryKey[]): void {
  keys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
}

/** Claiming a course self-enrol slug grants an enrollment and can complete a profile. */
export const SELF_ENROLL_INVALIDATION_KEYS: readonly QueryKey[] = [
  ENROLLMENTS_KEY,
  PROFILE_KEY,
];

/**
 * Claiming an invite link joins an organization: the membership list on the
 * profile drives the org switcher, so the profile is as stale as the roster.
 * Event links produce an event registration instead of an enrollment.
 */
export const INVITE_CLAIM_INVALIDATION_KEYS: readonly QueryKey[] = [
  ENROLLMENTS_KEY,
  ORGANIZATIONS_KEY,
  PROFILE_KEY,
];

/** Covers invoice detail (`['invoices', String(id)]`) and the admin list (`['invoices', 'list', params]`). */
export const INVOICES_KEY: QueryKey = ['invoices'];

/** `['admin', 'users', userId, 'invoices']` — a single user's invoice history. */
export function isAdminUserInvoicesKey(key: QueryKey): boolean {
  return key[0] === 'admin' && key[1] === 'users' && key[3] === 'invoices';
}

/**
 * Every cache showing invoice money. Registration mutations (cancel, transfer,
 * substitute, status change) add or void invoice line items server-side, so an
 * invoice the admin already has on screen is wrong the moment one returns.
 * The per-user history needs a predicate because the mutation does not know
 * whose invoice the server touched.
 */
export function invalidateInvoiceCaches(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: INVOICES_KEY });
  queryClient.invalidateQueries({
    predicate: (query) => isAdminUserInvoicesKey(query.queryKey),
  });
}

/**
 * Server state whose meaning depends on the `X-Active-Org-Id` header, dropped
 * when the active context changes. Deliberately excludes `['profile']`:
 * identity and roles are the same in every context.
 *
 * This is a backstop, not the primary defence. An org-scoped response must
 * also carry the org in its own query key (see discountsQueryKey) so a second
 * organization is a cache miss rather than a hit on the first one's data.
 */
export const ORG_SCOPED_QUERY_ROOTS = [
  'organizations',
  'course_packages',
  'enrollments',
  'events',
  'employee_analytics',
  'billing',
  'discounts',
] as const;

export function invalidateOrgScopedQueries(queryClient: QueryClient): void {
  ORG_SCOPED_QUERY_ROOTS.forEach((root) => queryClient.invalidateQueries({ queryKey: [root] }));
}
